import { makeAutoObservable, runInAction } from 'mobx';
import { agent } from '../api/agent';
import { toast } from 'react-toastify';
import {
  ReportQueryParams,
  OccupancyReportDTO,
  RevenueReportDTO,
  PaymentsReportDTO,
  LostRevenueReportDTO,
  ClientsReportDTO,
  BookingsOverviewReportDTO,
} from '../models/Reporting';

export type ReportType =
  | 'occupancy'
  | 'revenue'
  | 'payments'
  | 'lostRevenue'
  | 'clients'
  | 'bookingsOverview';

export default class ReportingStore {
  occupancy: OccupancyReportDTO | null = null;
  revenue: RevenueReportDTO | null = null;
  payments: PaymentsReportDTO | null = null;
  lostRevenue: LostRevenueReportDTO | null = null;
  clients: ClientsReportDTO | null = null;
  bookingsOverview: BookingsOverviewReportDTO | null = null;

  loading: Record<ReportType, boolean> = {
    occupancy: false,
    revenue: false,
    payments: false,
    lostRevenue: false,
    clients: false,
    bookingsOverview: false,
  };

  constructor() {
    makeAutoObservable(this);
  }

  private setLoading = (type: ReportType, val: boolean) => {
    runInAction(() => { this.loading[type] = val; });
  };

  loadOccupancy = async (params: ReportQueryParams) => {
    this.setLoading('occupancy', true);
    try {
      const data = await agent.Reports.occupancy(params);
      runInAction(() => { this.occupancy = data; });
    } catch {
      toast.error('Failed to load occupancy report');
    } finally {
      this.setLoading('occupancy', false);
    }
  };

  loadRevenue = async (params: ReportQueryParams) => {
    this.setLoading('revenue', true);
    try {
      const data = await agent.Reports.revenue(params);
      runInAction(() => { this.revenue = data; });
    } catch {
      toast.error('Failed to load revenue report');
    } finally {
      this.setLoading('revenue', false);
    }
  };

  loadPayments = async (params: ReportQueryParams) => {
    this.setLoading('payments', true);
    try {
      const data = await agent.Reports.payments(params);
      runInAction(() => { this.payments = data; });
    } catch {
      toast.error('Failed to load payments report');
    } finally {
      this.setLoading('payments', false);
    }
  };

  loadLostRevenue = async (params: ReportQueryParams) => {
    this.setLoading('lostRevenue', true);
    try {
      const data = await agent.Reports.lostRevenue(params);
      runInAction(() => { this.lostRevenue = data; });
    } catch {
      toast.error('Failed to load lost revenue report');
    } finally {
      this.setLoading('lostRevenue', false);
    }
  };

  loadClients = async (params: ReportQueryParams) => {
    this.setLoading('clients', true);
    try {
      const data = await agent.Reports.clients(params);
      runInAction(() => { this.clients = data; });
    } catch {
      toast.error('Failed to load clients report');
    } finally {
      this.setLoading('clients', false);
    }
  };

  loadBookingsOverview = async (params: ReportQueryParams) => {
    this.setLoading('bookingsOverview', true);
    try {
      const data = await agent.Reports.bookingsOverview(params);
      runInAction(() => { this.bookingsOverview = data; });
    } catch {
      toast.error('Failed to load bookings overview');
    } finally {
      this.setLoading('bookingsOverview', false);
    }
  };

  exportClientsCsv = (clients: ClientsReportDTO) => {
    const header = 'Full Name,Phone,Email,Total Bookings,Total Spent,First Booking,Last Booking,Most Used Asset,Venue';
    const rows = clients.clients.map(c =>
      [
        `"${c.customerFullName}"`,
        `"${c.customerPhone}"`,
        `"${c.customerEmail}"`,
        c.totalBookings,
        c.totalSpent.toFixed(2),
        new Date(c.firstBookingDate).toLocaleDateString('en-CA'),
        new Date(c.lastBookingDate).toLocaleDateString('en-CA'),
        `"${c.mostUsedAssetName}"`,
        `"${c.venueName}"`,
      ].join(',')
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `clients_${new Date().toLocaleDateString('en-CA')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  exportBookingsCsv = (overview: BookingsOverviewReportDTO) => {
    const header = 'Customer,Phone,Asset,Venue,Start,End,Capacity,Price,Payment,Recurring,Status';
    const payLabel = (v: number) => v === 1 ? 'Card' : v === 2 ? 'Cash' : 'Unpaid';
    const rows = overview.bookings.map(b =>
      [
        `"${b.customerFullName}"`,
        `"${b.customerPhone}"`,
        `"${b.assetName}"`,
        `"${b.venueName}"`,
        new Date(b.dateTimeStart).toLocaleString('en-CA'),
        new Date(b.dateTimeEnd).toLocaleString('en-CA'),
        b.capacityUsed,
        b.priceFinal.toFixed(2),
        payLabel(b.isPayed),
        b.isRecurring ? 'Yes' : 'No',
        `"${b.currentStatus}"`,
      ].join(',')
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `bookings_${new Date().toLocaleDateString('en-CA')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  clearAll = () => {
    runInAction(() => {
      this.occupancy = null;
      this.revenue = null;
      this.payments = null;
      this.lostRevenue = null;
      this.clients = null;
      this.bookingsOverview = null;
    });
  };
}